import React, { useState } from 'react';
import {
    Box,
    Stepper,
    Step,
    StepLabel,
    Button,
    DialogContent,
    CircularProgress,
    Backdrop,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Swal from 'sweetalert2';
import { t } from 'i18next';
import { createReservation, updateReservation, getRoomAvailability } from '../../api/reservationApi';
import { useUserProfile } from '@shared/hooks/useUserProfile';
import RenderStepContent from './RenderStepContent/RenderStepContent';
import { steps, STEPS as STEPS_RESERVATION, } from './constants';
import { validationSchema, nightsSchema } from './reservationSchema';
import {
    RESERVATION_MODE,
    RESERVATION_STATUS,
    AVAILABILITY_ENDPOINT,
    DEFAULT_MAX_GUESTS,
} from '../../constants';
import {
    buildInitialReservationData,
    buildReservationPayload,
    validateRoomDistribution,
} from '../../lib/reservationRules';

const ReservationFlow = ({
    handleCloseModal,
    mode = RESERVATION_MODE.CREATE,
    initialData = null,
    onReservationCompleted,
    vacationHomes,
}) => {

    const { userData, isReady } = useUserProfile();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [availableRooms, setAvailableRooms] = useState(initialData?.assignedRooms || []);
    const [error, setError] = useState('');
    const [validateFields, setValidateFields] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [maxGuestsAllowed, setMaxGuestsAllowed] = useState(DEFAULT_MAX_GUESTS);
    const [fieldErrors, setFieldErrors] = useState({});
    const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

    const [reservationData, setReservationData] = useState(() => {
        // Una reserva rechazada por CEO no es editable: avisa y cierra.
        if (mode === RESERVATION_MODE.EDIT && initialData?.status === RESERVATION_STATUS.REJECTED_CEO) {
            Swal.fire({
                icon: 'error',
                title: t('errorCannotEditRejectedReservationTitle'),
                text: t('errorCannotEditRejectedReservationMessage'),
            });
            handleCloseModal();
            return {};
        }
        return buildInitialReservationData(mode, initialData, vacationHomes);
    });

    const [completedSteps, setCompletedSteps] = useState(
        Array(steps.length).fill(false)
    );

    const stepValidationStrategies = {
        [STEPS_RESERVATION.SELECT_LOCATION]: async ({ reservationData, validationSchema, mode }) => {
            await validationSchema.validateAt('location', {
                ...reservationData,
                isEditMode: mode === RESERVATION_MODE.EDIT
            }, { abortEarly: false });

            if (!reservationData.location) {
                throw new Error(t('errorSelectLocation'));
            }

            return { isValid: true };
        },

        [STEPS_RESERVATION.SELECT_DATES]: async ({ reservationData, nightsSchema, mode, validateDates, initialData }) => {
            const datesValid = await validateDates();
            if (!datesValid) {
                throw new Error(t('unexpectedValidationError'));
            }

            await nightsSchema.validate({
                ...reservationData,
                isEditMode: mode === RESERVATION_MODE.EDIT
            }, { abortEarly: false });

            if (mode === RESERVATION_MODE.EDIT && initialData && reservationData.numberOfRooms > 0) {
                const originalStartDate = initialData.startDate
                    ? new Date(initialData.startDate).toISOString().split('T')[0]
                    : '';
                const originalEndDate = initialData.endDate
                    ? new Date(initialData.endDate).toISOString().split('T')[0]
                    : '';

                const datesChanged =
                    reservationData.startDate !== originalStartDate ||
                    reservationData.endDate !== originalEndDate;

                if (!datesChanged) {
                    return { isValid: true, skipToNextStep: true };
                }
            }

            if (!reservationData.isAvailabilityChecked) {
                throw new Error(t('errorCheckAvailabilityBeforeProceeding'));
            }

            if(reservationData.isAvailabilityChecked && reservationData.numberOfGuests <= 0) {
                throw new Error(t('errorNoGuests'));
            }

            return { isValid: true };
        },

        [STEPS_RESERVATION.SELECT_ROOMS]: async ({ reservationData, availableRooms, mode, setReservationData }) => {
            if (mode !== RESERVATION_MODE.EDIT && (reservationData.numberOfRooms < 1 || reservationData.numberOfRooms > availableRooms.length)) {
                throw new Error(t('errorNumberOfRooms'));
            }

            validateRoomsWithGuests();

            const assignedRooms = availableRooms.slice(0, reservationData.numberOfRooms);
            setReservationData(prevData => ({
                ...prevData,
                assignedRooms
            }));

            return { isValid: true };
        }
    };

    const resetValidationState = (setValidateFields, setError, setFieldErrors) => {
        setValidateFields(false);
        setError('');
        setFieldErrors({});
    };

    const advanceStep = (setActiveStep, setCompletedSteps, currentStep) => {
        setActiveStep(prevStep => prevStep + 1);
        setCompletedSteps(prev => {
            const updatedSteps = [...prev];
            updatedSteps[currentStep] = true;
            return updatedSteps;
        });
    };

    const handleValidationError = (err, setFieldErrors, setError, t) => {
        if (err.inner && err.inner.length > 0) {
            const fieldErrors = {};
            err.inner.forEach((validationError) => {
                fieldErrors[validationError.path] = validationError.message;
            });
            setFieldErrors(fieldErrors);
        } else if (err.errors && Array.isArray(err.errors)) {
            setError(err.errors.join(', '));
        } else if (err.message) {
            setError(err.message);
        } else {
            console.error('Validation Error:', err);
            setError(t('unexpectedValidationError'));
        }
    };

    const handleNext = async () => {
        setValidateFields(true);
        setFieldErrors({});
        try {
            const validationStrategy = stepValidationStrategies[activeStep];

            if (!validationStrategy) {
                throw new Error(`No validation strategy found for step ${activeStep}`);
            }

            let validationResult;
            switch (activeStep) {
                case STEPS_RESERVATION.SELECT_LOCATION:
                    validationResult = await validationStrategy({
                        reservationData,
                        validationSchema,
                        mode
                    });
                    break;

                case STEPS_RESERVATION.SELECT_DATES:
                    validationResult = await validationStrategy({
                        reservationData,
                        validationSchema,
                        nightsSchema,
                        mode,
                        validateDates
                    });
                    break;

                case STEPS_RESERVATION.SELECT_ROOMS:
                    validationResult = await validationStrategy({
                        reservationData,
                        availableRooms,
                        mode,
                        setReservationData
                    });
                    break;

                default:
                    validationResult = { isValid: true };
            }

            if (validationResult.isValid) {
                advanceStep(setActiveStep, setCompletedSteps, activeStep);
                resetValidationState(setValidateFields, setError, setFieldErrors);
            }
        } catch (err) {
            handleValidationError(err, setFieldErrors, setError, t);
        }
    };

    const handleBack = () => {
        const updatedCompletedSteps = [...completedSteps];
        updatedCompletedSteps[activeStep] = false;
        setCompletedSteps(updatedCompletedSteps);

        setActiveStep((prevStep) => prevStep - 1);
        setValidateFields(false);
        setError('');
        setFieldErrors({});
    };

    const handleDisabledNextButton = () => {
        const { location, numberOfRooms, startDate, endDate } = reservationData;

        const validationStep = {
            [STEPS_RESERVATION.SELECT_LOCATION]: activeStep === STEPS_RESERVATION.SELECT_LOCATION && location === '',
            [STEPS_RESERVATION.SELECT_DATES]: activeStep === STEPS_RESERVATION.SELECT_DATES && startDate === endDate,
            [STEPS_RESERVATION.SELECT_ROOMS]:  activeStep === STEPS_RESERVATION.SELECT_ROOMS && numberOfRooms === 0,
        };

        return validationStep[activeStep];
    }

    const handleReservation = async () => {
        setIsSubmitting(true);
        try {
            if (!isReady || !userData) {
                setError(t('errorFetchingUserData'));
                setIsSubmitting(false);
                return;
            }

            const selectedLocation = vacationHomes.find(loc => loc.name === reservationData.location);
            if (!selectedLocation) {
                setError(t('errorSelectLocation'));
                setIsSubmitting(false);
                return;
            }

            const reservationDataToSend = buildReservationPayload(
                reservationData,
                userData,
                selectedLocation,
                mode,
                initialData
            );

            let response;

            if (mode === RESERVATION_MODE.CREATE) {
                response = await createReservation(reservationDataToSend);
                if (response.status === 201) {
                    Swal.fire({
                        icon: 'success',
                        title: t('reservationSuccessTitle'),
                        text: t('reservationSuccessMessage'),
                        confirmButtonText: 'OK',
                    });
                } else {
                    throw new Error('Error al crear la reserva');
                }
            } else {
                response = await updateReservation(reservationDataToSend);
                if (response.status === 200) {
                    Swal.fire({
                        icon: 'success',
                        title: t('reservationUpdateSuccessTitle'),
                        text: t('reservationUpdateSuccessMessage'),
                        confirmButtonText: 'OK',
                    });
                } else {
                    throw new Error('Error al actualizar la reserva');
                }
            }
            onReservationCompleted(response.data?.data);
        } catch (error) {
            console.error('Error al procesar la reserva:', error);
            Swal.fire({
                icon: 'error',
                title: t('reservationErrorTitle'),
                text: t('reservationErrorMessage'),
                confirmButtonText: t('contactSupport'),
            });
        } finally {
            setIsSubmitting(false);
        }

        handleCloseModal();
    };

    const getStepIcon = (index) => {
        if (completedSteps[index]) {
            return <CheckCircleIcon color="primary" />;
        }
        return <RadioButtonUncheckedIcon color="disabled" />;
    };

    const validateDates = async () => {
        try {
            await validationSchema.validateAt('startDate', {
                ...reservationData,
                isEditMode: mode === RESERVATION_MODE.EDIT
            }, { abortEarly: false });

            await validationSchema.validateAt('endDate', {
                ...reservationData,
                isEditMode: mode === RESERVATION_MODE.EDIT
            }, { abortEarly: false });

            await nightsSchema.validate({
                ...reservationData,
                isEditMode: mode === RESERVATION_MODE.EDIT
            }, { abortEarly: false });

            setFieldErrors({});
            return true;
        } catch (err) {
            const errors = {};

            if (err.inner && err.inner.length > 0) {
                err.inner.forEach((validationError) => {
                    errors[validationError.path] = validationError.message;
                });
            } else if (err.path && err.message) {
                errors[err.path] = err.message;
            } else {
                console.error('Validation Error:', err);
                setError(t('unexpectedValidationError'));
                return false;
            }

            setFieldErrors(errors);
            return false;
        }
    };

    const validateRoomsWithGuests = () => {
        const selectedLocation = vacationHomes.find(loc => loc.name === reservationData.location);
        return validateRoomDistribution(reservationData, selectedLocation, t);
    };

    const fetchRoomAvailability = async (endpoint, params) => {
        try {
            const response = await getRoomAvailability(endpoint, params);

            if (response.status === 200) {
                const available = response.data.available;

                if(!available) {
                    return {
                        success: false,
                        error: response.data.message || t("errorFetchingAvailableRooms")
                    };
                }

                return {
                    success: true,
                    data: response.data
                };
            }

            return {
                success: false,
                error: t("errorFetchingAvailableRooms")
            };
        } catch (error) {
            console.error("Error fetching available rooms:", error);
            return {
                success: false,
                error: error.response?.data?.message || t("errorFetchingAvailableRooms")
            };
        }
    };

    const checkAvailability = async () => {
        setError('');

        const params = {
            location: reservationData.location,
            startDate: reservationData.startDate,
            endDate: reservationData.endDate
        };

        try {
            if (mode === RESERVATION_MODE.EDIT) {
                return await handleEditModeAvailability(params);
            }
            return await handleCreateModeAvailability(params);
        } catch (error) {
            handleAvailabilityError(error);
            return false;
        }
    };

    const handleEditModeAvailability = async (params) => {
        // En edición reconsultamos disponibilidad SIEMPRE, aunque las fechas no cambien.
        // Antes, con fechas iguales se hacía un early-return que dejaba `availableRooms`
        // con los cuartos YA asignados a la reserva: el colaborador no podía SUBIR de
        // cuartos (p.ej. al agregar huéspedes), solo quedarse con los que tenía.
        // Usamos el endpoint by-user con el id de la reserva en edición para que la
        // validación de solapamiento excluya su propia reserva.
        const byUserParams = { ...params, reservationId: initialData?.id };
        const availabilityResponse = await fetchRoomAvailability(AVAILABILITY_ENDPOINT.BY_USER, byUserParams);
        if (!availabilityResponse.success) {
            throw new Error(availabilityResponse.error);
        }

        updateAvailabilityState(availabilityResponse.data);
        return true;
    };

    const handleCreateModeAvailability = async (params) => {
        const [availabilityResponse, userAvailabilityResponse] = await Promise.all([
            fetchRoomAvailability(AVAILABILITY_ENDPOINT.GENERAL, params),
            fetchRoomAvailability(AVAILABILITY_ENDPOINT.BY_USER, params)
        ]);

        if (!availabilityResponse.success || !userAvailabilityResponse.success) {
            throw new Error(availabilityResponse.error || userAvailabilityResponse.error);
        }

        const { availableRooms, available } = availabilityResponse.data;
        if (!isAvailabilityValid(availableRooms, available, userAvailabilityResponse.data.available)) {
            throw new Error(t('noAvailabilityOnSelectedDates'));
        }

        updateAvailabilityState(availabilityResponse.data);
        return true;
    };

    const updateAvailabilityState = (data) => {
        const selectedLocation = vacationHomes.find(loc => loc.name === reservationData.location);
        const maxGuests = selectedLocation.maximumGuests;

        setAvailableRooms(data.availableRooms);
        setMaxGuestsAllowed(maxGuests);
        updateReservationState(true);
    };

    const updateReservationState = (isAvailabilityChecked) => {
        setReservationData(prev => ({
            ...prev,
            isAvailabilityChecked
        }));
    };

    const handleAvailabilityError = (error) => {
        setError(error.message);
        setAvailableRooms([]);
        updateReservationState(false);
    };

    const isAvailabilityValid = (availableRooms, available, userAvailable) => {
        return availableRooms.length > 0 && available && userAvailable;
    };

    if (!isReady) {
        return (
            <DialogContent
                sx={{
                    minHeight: '55vh',
                    maxHeight: '55vh',
                    overflowY: 'auto',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <CircularProgress />
            </DialogContent>
        );
    }

    return (
        <>
            <DialogContent sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <Stepper activeStep={activeStep} sx={{ mb: 4, px: 2 }}>
                    {steps.map((label, index) => (
                        <Step key={index}>
                            <StepLabel StepIconComponent={() => getStepIcon(index)}>
                                {t(label)}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Box>
                    <RenderStepContent
                        step={activeStep}
                        reservationData={reservationData}
                        setReservationData={setReservationData}
                        validateFields={validateFields}
                        handleReservation={handleReservation}
                        error={error}
                        fieldErrors={fieldErrors}
                        setFieldErrors={setFieldErrors}
                        setError={setError}
                        isEditMode={mode === RESERVATION_MODE.EDIT}
                        existingReservation={initialData}
                        vacationHomes={vacationHomes}
                        availableRooms={availableRooms}
                        checkAvailability={checkAvailability}
                        maxGuestsAllowed={maxGuestsAllowed}
                        validateDates={validateDates}
                        setAvailableRooms={setAvailableRooms}
                        isCheckingAvailability={isCheckingAvailability}
                        setIsCheckingAvailability={setIsCheckingAvailability}
                    />
                </Box>

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
                    <Button variant="contained" color="inherit" disabled={activeStep === 0} onClick={handleBack}>
                        {t('back')}
                    </Button>
                    {activeStep === steps.length - 1 ? (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleReservation}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <CircularProgress size={24} color="inherit" />
                                    &nbsp;
                                    {mode === RESERVATION_MODE.CREATE
                                        ? t('confirmingReservation')
                                        : t('updatingReservation')}
                                </>
                            ) : mode === RESERVATION_MODE.CREATE ? (
                                t('confirmReservation')
                            ) : (
                                t('updateReservation')
                            )}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleNext}
                            disabled={handleDisabledNextButton()}
                        >
                            {t('next')}
                        </Button>

                    )}
                </Box>
            </DialogContent>

            <Backdrop open={isSubmitting} style={{ zIndex: 9999 }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    );
};

export default ReservationFlow;
